// src/components/Admin/FirmIndustryInsightsCard.js
// Firm Portal card for Industry Insights Summary - placed in Analytics section (Enterprise tier only)

import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    Tooltip,
    IconButton,
    Skeleton
} from '@mui/material';
import {
    Analytics as AnalyticsIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import IndustryInsightsSummaryModal from '../IndustryInsightsSummaryModal';

// Default industry taxonomy (matches taxonomyUtils.js)
const DEFAULT_INDUSTRY_TAXONOMY = [
    'Manufacturing',
    'Healthcare',
    'Financial Services',
    'Professional Services',
    'Technology',
    'Retail & E-commerce',
    'Real Estate',
    'Construction',
    'Transportation & Logistics',
    'Hospitality & Food Service',
    'Energy & Utilities'
];

const FirmIndustryInsightsCard = ({ organizationId }) => {
    const { currentUser } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [industries, setIndustries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Fetch industries: Use firm taxonomy if set, otherwise use ICR-generated from clients
    useEffect(() => {
        const fetchIndustries = async () => {
            if (!organizationId) return;

            try {
                setIsLoading(true);

                // First, check if org has custom taxonomy
                const orgRef = doc(db, 'organizations', organizationId);
                const orgSnap = await getDoc(orgRef);
                const orgData = orgSnap.exists() ? orgSnap.data() : {};

                if (orgData.industryTaxonomy && orgData.industryTaxonomy.length > 0) {
                    // Use firm's custom taxonomy
                    setIndustries(orgData.industryTaxonomy);
                } else {
                    // Fall back to industries from clients (use canonical for aggregation)
                    const clientsRef = collection(db, 'clients');
                    const q = query(clientsRef, where('organizationId', '==', organizationId));
                    const snapshot = await getDocs(q);

                    // Extract unique industries from clients using industryCanonical
                    const industrySet = new Set();
                    snapshot.docs.forEach(doc => {
                        const industry = doc.data().industryCanonical || doc.data().industry;
                        if (industry && industry.trim()) {
                            industrySet.add(industry.trim());
                        }
                    });

                    // Convert to sorted array
                    const uniqueIndustries = Array.from(industrySet).sort();
                    
                    // If no client industries found, use default taxonomy
                    if (uniqueIndustries.length === 0) {
                        setIndustries(DEFAULT_INDUSTRY_TAXONOMY);
                    } else {
                        setIndustries(uniqueIndustries);
                    }
                }
            } catch (error) {
                console.error('Error fetching industries:', error);
                // Fall back to defaults on error
                setIndustries(DEFAULT_INDUSTRY_TAXONOMY);
            } finally {
                setIsLoading(false);
            }
        };

        fetchIndustries();
    }, [organizationId]);

    if (isLoading) {
        return (
            <Card sx={{ height: '100%' }}>
                <CardContent>
                    <Skeleton variant="text" width="60%" height={32} />
                    <Skeleton variant="text" width="100%" />
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="rectangular" height={36} sx={{ mt: 2 }} />
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card sx={{ height: '100%' }}>
                <CardContent>
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            mb: 1
                        }}
                    >
                        <Typography variant="h6" fontWeight={600}>
                            Industry Insights
                        </Typography>
                        <Tooltip title="Cross-client analysis across your firm's clients in the same industry. Identifies patterns, challenges, opportunities, and actionable recommendations.">
                            <IconButton size="small">
                                <InfoIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ mb: 2 }}
                    >
                        Analyze patterns across all firm clients in a selected industry.
                    </Typography>

                    <Button
                        variant="contained"
                        startIcon={<AnalyticsIcon />}
                        onClick={() => setIsModalOpen(true)}
                        fullWidth
                        size="small"
                    >
                        Generate Report
                    </Button>
                </CardContent>
            </Card>

            {/* Modal */}
            <IndustryInsightsSummaryModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                industries={industries}
                scope="firm"
                user={currentUser}
            />
        </>
    );
};

export default FirmIndustryInsightsCard;